import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, GripHorizontal } from "lucide-react"
import { cn } from "@/lib/utils"
import { useReturnFocusOnUnmount } from '@/lib/a11y/useReturnFocusOnUnmount'
import { resolveOpener } from "@/lib/a11y/menuOpener"
import { modalBand, nextModalDepth, registerModal } from "@/lib/layers/layerStack"
import { LayerOwnerProvider, useLayerOwner, type LayerOwner } from "@/lib/layers/LayerOwner"
import { overlay, type DialogSize } from "./overlay-recipes"

/**
 * Two layouts (docs/THEME.md 2.9):
 * - "legacy" (the default): every existing DialogContent variant keeps its DOM, padding, z values and scroll exactly.
 * - "sections" (opt-in, migrated callers): a NON-scrolling root with DialogHeader / DialogBody / DialogFooter, where
 *   DialogBody is the only scroll region and the footer actions stay reachable at 320px height; width by `size`; z by
 *   modal depth (lib/layers/layerStack.ts); and an owned layer slot so Select / menu / popover / tooltip opened inside
 *   it render above it and inside its focus scope (2.10).
 */
type DialogLayout = "legacy" | "sections"
const DialogLayoutContext = React.createContext<DialogLayout>("legacy")

const useIsoLayoutEffect = typeof window === "undefined" ? React.useEffect : React.useLayoutEffect

/**
 * Whether the enclosing Dialog is open (null when DialogContent is used without our Dialog root). DialogContent itself
 * renders while closed (only its portal is gated), so the depth registry must follow the open state, not mounting.
 */
const DialogOpenContext = React.createContext<boolean | null>(null)

/**
 * This modal's depth, fixed for as long as it is open: legacy variants pass the depth their z already is; a sections
 * dialog takes one above the deepest open modal, and at least one above the modal it is rendered inside (so a child
 * dialog mounted in the same render as its parent still lands above it). Registered only while open; the depth is
 * taken at the moment it opens and kept through the closing animation.
 */
function useModalDepth(opts: { fixedDepth?: number; minDepth?: number }) {
  const parent = useLayerOwner()
  const open = React.useContext(DialogOpenContext) ?? true
  const parentDepth = parent?.kind === "modal" ? parent.depth : 0
  const parentKey = parent?.kind === "modal" ? parent.modalKey ?? null : null
  const { fixedDepth, minDepth } = opts
  // This modal's identity, known from the first render so a child can name it as its parent before it registers.
  const keyRef = React.useRef<symbol | null>(null)
  if (keyRef.current === null) keyRef.current = Symbol("modal")
  const key = keyRef.current
  const pick = React.useCallback(
    () => fixedDepth ?? Math.max(nextModalDepth(minDepth, key), parentDepth + 1),
    [fixedDepth, minDepth, parentDepth, key],
  )
  // A first guess for the first render; the real depth is taken in the commit, AFTER any dialog that is closing in the
  // same update has released its depth. A top-level modal takes it in a layout effect (before paint). A modal inside
  // another takes it in a passive effect, which runs after every layout effect of the commit: React runs a child's
  // effects before its parent's, so a child registering first would push a parent mounted with it one depth too high.
  const nested = parent?.kind === "modal"
  const [depth, setDepth] = React.useState(pick)
  const take = React.useCallback(() => {
    const next = pick()
    setDepth(next)
    return registerModal({ fixedDepth: next, key, parent: parentKey }).release
  }, [pick, key, parentKey])
  // Re-taken when the dialog opens or its parent's depth changes; kept through the closing animation.
  useIsoLayoutEffect(() => (open && !nested ? take() : undefined), [open, nested, take])
  React.useEffect(() => (open && nested ? take() : undefined), [open, nested, take])
  return { depth, key }
}

// WCAG 2.4.3: Radix only restores focus to a <DialogTrigger>; a controlled dialog (open prop, no trigger) dropped focus to <body>.
// The Root remembers the element focused when `open` turned true; the Content hands focus back to it on close.
const OpenerContext = React.createContext<React.MutableRefObject<HTMLElement | null> | null>(null)

const Dialog = (props: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  // The open state, tracked the way Radix tracks it, so the modal depth registry follows it (Root stays as given).
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(props.defaultOpen ?? false)
  const open = props.open ?? uncontrolledOpen
  const { onOpenChange } = props
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange],
  )
  const openerRef = React.useRef<HTMLElement | null>(null)
  const wasOpenRef = React.useRef(false)
  const isOpen = !!props.open
  if (isOpen !== wasOpenRef.current) {
    wasOpenRef.current = isOpen
    // Read while rendering: nothing inside the dialog has taken focus yet.
    // A focused menu item is about to unmount with its menu: remember the menu's trigger instead.
    if (isOpen && typeof document !== 'undefined') openerRef.current = resolveOpener(document.activeElement)
  }
  return (
    <OpenerContext.Provider value={openerRef}>
      <DialogOpenContext.Provider value={open}>
        <DialogPrimitive.Root {...props} onOpenChange={handleOpenChange} />
      </DialogOpenContext.Provider>
    </OpenerContext.Provider>
  )
}

function useRestoreOpenerFocus(onCloseAutoFocus?: (event: Event) => void) {
  const openerRef = React.useContext(OpenerContext)
  return React.useCallback(
    (event: Event) => {
      onCloseAutoFocus?.(event)
      if (event.defaultPrevented) return
      const opener = openerRef?.current
      if (opener && opener !== document.body && opener.isConnected) {
        event.preventDefault()
        opener.focus()
      }
    },
    [openerRef, onCloseAutoFocus],
  )
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

function useResetBodyPointerEvents() {
  React.useEffect(() => {
    return () => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    };
  }, []);
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  // Safety net: reset body pointer-events on unmount (Radix can leave stale state)
  useResetBodyPointerEvents();

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[10000] bg-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  );
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type LegacyContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>

export interface DialogContentProps extends LegacyContentProps {
  /** "sections" opts into the standard structure (2.9). Default "legacy": nothing about the dialog changes. */
  layout?: DialogLayout
  /** layout="sections" only: max width, sm 480 / md 640 (today's max-w-lg) / lg 840 / xl 1120px. */
  size?: DialogSize
  /** layout="sections" only: "left" makes it a slide-out drawer anchored to the left edge (the mobile Settings menu). Default: centred. */
  side?: "left"
  /** layout="sections" only: the lowest modal depth (z band) this dialog may take, e.g. 3 for the app-wide confirm. */
  minDepth?: number
  /** Opt-in for a page state whose previous cover obscured all content. Default keeps bg-backdrop. */
  backdrop?: "dim" | "opaque"
  /** layout="sections" only: omit the X close button (AlertDialog). */
  hideClose?: boolean
  /** layout="sections" only: the close button's accessible name. */
  closeLabel?: string
  /** Opt-in for non-modal persistent notices: page popup band, with outside focus preserved. */
  focusPolicy?: "dialog" | "preserve"
  /** layout="sections" only: attributes for the close button (a test id), so a dialog keeps the id it had on its own X. */
  closeProps?: Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>, "children" | "className" | "asChild"> &
    Partial<Record<`data-${string}`, string>>
}

const LegacyDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  LegacyContentProps
>(({ className, children, onCloseAutoFocus, ...props }, ref) => {
  // Safety net: reset body pointer-events on unmount
  useResetBodyPointerEvents();
  const restoreFocus = useRestoreOpenerFocus(onCloseAutoFocus);
  useModalDepth({ fixedDepth: 1 });

  return (
  <DialogPortal container={typeof document !== 'undefined' ? document.body : undefined}>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onCloseAutoFocus={restoreFocus}
      className={cn(
        "fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-edge bg-panel p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-sm text-ink max-h-[100dvh] overflow-y-auto [animation-fill-mode:backwards]",
        className
      )}
      {...props}
    >
      <LegacyOwnerReset>{children}</LegacyOwnerReset>
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
LegacyDialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * Inside a legacy dialog, popups keep today's behaviour (body portal at their own z), even under a page-level owner:
 * a popup in `#layer-root` at z-page-layer would sit BELOW the legacy dialog's z 10001 (2.10, "an unmigrated dialog").
 */
function LegacyOwnerReset({ children }: { children?: React.ReactNode }) {
  return <LayerOwnerProvider value={null}>{children}</LayerOwnerProvider>
}

/**
 * A persistent non-modal notice uses the same sections shell and page-owned popup band. It has no Radix
 * DismissableLayer or FocusScope: arriving must not steal focus, block an existing menu's Escape, or trap Tab.
 * The proven notice focus-return hook restores focus only when it was inside the notice when its action vanished.
 */
const PersistentDialogContent = React.forwardRef<HTMLDivElement, LegacyContentProps & { size?: DialogSize }>(
  ({ className, children, size = "md", forceMount: _forceMount,
    onOpenAutoFocus: _onOpenAutoFocus, onCloseAutoFocus: _onCloseAutoFocus,
    onEscapeKeyDown: _onEscapeKeyDown, onPointerDownOutside: _onPointerDownOutside,
    onInteractOutside: _onInteractOutside, onFocusOutside: _onFocusOutside, ...props }, ref) => {
    const open = React.useContext(DialogOpenContext) ?? true;
    const opener = React.useContext(OpenerContext);
    const node = React.useRef<HTMLDivElement | null>(null);
    useReturnFocusOnUnmount(open, node, opener ?? undefined);
    const [slot, setSlot] = React.useState<HTMLDivElement | null>(null);
    const owner = React.useMemo<LayerOwner>(() => ({ kind: "page", depth: 0, slot }), [slot]);
    const setNode = React.useCallback((element: HTMLDivElement | null) => {
      node.current = element;
      if (typeof ref === "function") ref(element);
      else if (ref) ref.current = element;
    }, [ref]);
    // Remove the focused action in this commit; Portal Presence otherwise delays it past focus-return cleanup.
    if (!open) return null;
    return (
      <DialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
        <div data-dialog-positioner="" className={cn(overlay.dialogPositioner, "z-page-layer")}>
          <div role="dialog" {...props} data-persistent-notice="" ref={setNode} data-layout="sections" data-size={size} data-layer-depth={0}
            className={cn(overlay.dialogSurface, overlay.dialogWidth[size], className)}>
            <DialogLayoutContext.Provider value="sections">
              <LayerOwnerProvider value={owner}>{children}</LayerOwnerProvider>
            </DialogLayoutContext.Provider>
            <div ref={setSlot} data-layer-slot="" />
          </div>
        </div>
      </DialogPortal>
    );
  },
);
PersistentDialogContent.displayName = "PersistentDialogContent";

const SectionsDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  Omit<DialogContentProps, "layout">
>(({ className, children, onCloseAutoFocus, size = "md", side, minDepth, backdrop = "dim", hideClose = false, closeLabel = "Close", closeProps, ...props }, ref) => {
  useResetBodyPointerEvents();
  const restoreFocus = useRestoreOpenerFocus(onCloseAutoFocus);
  const { depth, key: modalKey } = useModalDepth({ minDepth });
  const band = modalBand(depth);
  const [slot, setSlot] = React.useState<HTMLDivElement | null>(null);
  const owner = React.useMemo<LayerOwner>(() => ({ kind: "modal", depth, slot, modalKey }), [depth, slot, modalKey]);

  return (
    <DialogPortal container={typeof document !== 'undefined' ? document.body : undefined}>
      <DialogPrimitive.Overlay
        data-layer-depth={depth}
        className={cn(backdrop === "opaque" ? overlay.backdropOpaque : overlay.backdrop, "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0")}
        style={{ zIndex: band.backdrop }}
      />
      <div data-dialog-positioner="" className={side === "left" ? overlay.drawerPositioner : overlay.dialogPositioner} style={{ zIndex: band.content }}>
        <DialogPrimitive.Content
          ref={ref}
          onCloseAutoFocus={restoreFocus}
          data-layout="sections"
          data-size={size}
          data-side={side}
          data-layer-depth={depth}
          className={cn(
            side === "left" ? overlay.drawerSurface : [overlay.dialogSurface, overlay.dialogWidth[size]],
            side === "left"
              ? "duration-normal data-[state=open]:animate-in data-[state=closed]:animate-out [animation-fill-mode:backwards]"
              : "duration-normal data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 [animation-fill-mode:backwards]",
            className
          )}
          {...props}
        >
          <DialogLayoutContext.Provider value="sections">
            <LayerOwnerProvider value={owner}>
              {children}
              {!hideClose && (
                <DialogPrimitive.Close {...closeProps} className={cn(overlay.dialogClose, side === "left" && overlay.drawerClose)}>
                  <X className="icon" aria-hidden="true" />
                  <span className="sr-only">{closeLabel}</span>
                </DialogPrimitive.Close>
              )}
            </LayerOwnerProvider>
          </DialogLayoutContext.Provider>
          {/* The owned layer slot: last child of the NON-scrolling root, outside DialogBody (2.10). */}
          <div ref={setSlot} data-layer-slot="" />
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
})
SectionsDialogContent.displayName = "DialogContentSections"

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ layout = "legacy", size, side, minDepth, backdrop, hideClose, closeLabel, closeProps, focusPolicy, ...props }, ref) =>
  layout === "sections" ? (
    focusPolicy === "preserve"
      ? <PersistentDialogContent ref={ref} size={size} {...props} />
      : <SectionsDialogContent ref={ref} size={size} side={side} minDepth={minDepth} backdrop={backdrop} hideClose={hideClose} closeLabel={closeLabel} closeProps={closeProps} {...props} />
  ) : (
    <LegacyDialogContent ref={ref} {...props} />
  )
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const layout = React.useContext(DialogLayoutContext)
  return (
    <div
      className={cn(
        layout === "sections" ? overlay.dialogHeader : "flex flex-col space-y-1.5 text-center sm:text-left",
        className
      )}
      {...props}
    />
  )
}
DialogHeader.displayName = "DialogHeader"

/** layout="sections": the ONLY scroll region between the header and the footer (2.9). */
const DialogBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} data-dialog-body="" className={cn(overlay.dialogBody, className)} {...props} />
))
DialogBody.displayName = "DialogBody"

/**
 * layout="sections": a form around DialogBody + DialogFooter (never around the root), so the body keeps scrolling and
 * the footer's submit button stays a form button.
 */
const DialogForm = React.forwardRef<HTMLFormElement, React.FormHTMLAttributes<HTMLFormElement>>(({ className, ...props }, ref) => (
  <form ref={ref} className={cn("flex min-h-0 flex-1 flex-col", className)} {...props} />
))
DialogForm.displayName = "DialogForm"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const layout = React.useContext(DialogLayoutContext)
  return (
    <div
      className={cn(
        layout === "sections" ? overlay.dialogFooter : "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className
      )}
      {...props}
    />
  )
}
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const layout = React.useContext(DialogLayoutContext)
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        layout === "sections" ? overlay.dialogTitle : "text-lg font-semibold leading-none tracking-tight",
        className
      )}
      {...props}
    />
  )
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const layout = React.useContext(DialogLayoutContext)
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(layout === "sections" ? overlay.dialogDescription : "text-sm text-muted-foreground", className)}
      {...props}
    />
  )
})
DialogDescription.displayName = DialogPrimitive.Description.displayName

// Nested dialog content with higher z-index for dialogs within dialogs
// NOTE: Parent dialogs may use inline styles up to z-index: 99999, so nested must be higher
const DialogContentNested = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onCloseAutoFocus, ...props }, ref) => {
  const restoreFocus = useRestoreOpenerFocus(onCloseAutoFocus);
  useModalDepth({ fixedDepth: 2 });
  return (
  <DialogPortal container={typeof document !== 'undefined' ? document.body : undefined}>
    <DialogPrimitive.Overlay
      className="fixed inset-0 z-[100100] bg-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
    <DialogPrimitive.Content
      ref={ref}
      onCloseAutoFocus={restoreFocus}
      className={cn(
        "fixed left-[50%] top-[50%] z-[100101] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-edge bg-panel p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-sm text-ink max-h-[100dvh] overflow-y-auto [animation-fill-mode:backwards]",
        className
      )}
      {...props}
    >
      <LegacyOwnerReset>{children}</LegacyOwnerReset>
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContentNested.displayName = "DialogContentNested"

// Draggable, non-modal dialog content (no overlay/darkening)
const DialogContentDraggable = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onCloseAutoFocus, ...props }, ref) => {
  const restoreFocus = useRestoreOpenerFocus(onCloseAutoFocus);
  useModalDepth({ fixedDepth: 1 });
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStart = React.useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <DialogPortal container={typeof document !== 'undefined' ? document.body : undefined}>
      {/* No overlay - no page darkening */}
      <DialogPrimitive.Content
        ref={ref}
        aria-describedby={undefined}
        onCloseAutoFocus={restoreFocus}
        className={cn(
          "fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg gap-4 border border-edge bg-panel p-6 pt-0 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg text-ink max-h-[100dvh] overflow-y-auto [animation-fill-mode:backwards]",
          className
        )}
        style={{
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
        }}
        {...props}
      >
        {/* Drag handle header with close button */}
        <div
          onMouseDown={handleMouseDown}
          className={cn(
            "flex items-center h-8 cursor-move rounded-t-lg bg-floating/50 hover:bg-floating/70 transition-colors -mx-6 px-2",
            isDragging && "bg-floating"
          )}
        >
          <GripHorizontal className="w-4 h-4 text-ink-muted" />
          <div className="flex-1" />
          <DialogPrimitive.Close
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none p-1 hover:bg-floating/50"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </div>
        <div>
          <LegacyOwnerReset>{children}</LegacyOwnerReset>
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContentDraggable.displayName = "DialogContentDraggable"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogContentNested,
  DialogContentDraggable,
  DialogHeader,
  DialogBody,
  DialogForm,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
export type { DialogSize }
